import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AdvancedEditor } from "./AdvancedEditor";
import { Megaphone, Plus, Pencil, Trash2, Send, Loader2, Eye, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const emptyFilter = {
  mode: "all", date_from: "", date_to: "",
  exclude_purchased_product_ids: [], manual_agent_ids: [],
};

const emptyForm = {
  campaign_id: null, name: "", subject: "", body_html: "",
  promo_product_ids: [], recipient_filter: emptyFilter,
};

function absoluteImageUrl(url) {
  if (!url) return "";
  return url.startsWith("http") ? url : `${API}${url}`;
}

const TEST_RECIPIENT_KEY = "admin.campaigns.testRecipient";

export default function CampaignsAdmin() {
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState(() => {
    try { return localStorage.getItem(TEST_RECIPIENT_KEY) || ""; } catch { return ""; }
  });
  const [testingId, setTestingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [htmlPreview, setHtmlPreview] = useState(null);
  const nameInputRef = useRef(null);
  const subjectInputRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(TEST_RECIPIENT_KEY, testTo); } catch { /* ignore quota */ }
  }, [testTo]);

  const load = async () => {
    setLoading(true);
    try {
      const [cRes, pRes, aRes] = await Promise.all([
        axios.get(`${API}/api/admin/campaigns`, { withCredentials: true }),
        axios.get(`${API}/api/admin/products`, { withCredentials: true }),
        axios.get(`${API}/api/admin/agents`, { withCredentials: true }),
      ]);
      setCampaigns(cRes.data.campaigns || []);
      setProducts(pRes.data.products || []);
      setAgents(aRes.data || []);
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm);
    setPreviewCount(null);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setForm({
      campaign_id: c.campaign_id, name: c.name, subject: c.subject, body_html: c.body_html,
      promo_product_ids: c.promo_product_ids || [],
      recipient_filter: { ...emptyFilter, ...(c.recipient_filter || {}) },
    });
    setPreviewCount(null);
    setDialogOpen(true);
  };

  const setFilter = (patch) => {
    setForm((f) => ({ ...f, recipient_filter: { ...f.recipient_filter, ...patch } }));
    setPreviewCount(null);
  };

  const handlePreviewRecipients = async () => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/campaigns/preview-recipients`, form.recipient_filter, { withCredentials: true });
      setPreviewCount(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to compute recipients");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      nameInputRef.current?.focus();
      return;
    }
    if (!form.subject.trim()) {
      toast.error("Subject is required");
      subjectInputRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, subject: form.subject, body_html: form.body_html,
        promo_product_ids: form.promo_product_ids, recipient_filter: form.recipient_filter,
      };
      if (form.campaign_id) {
        await axios.put(`${API}/api/admin/campaigns/${form.campaign_id}`, payload, { withCredentials: true });
        toast.success("Campaign updated");
      } else {
        await axios.post(`${API}/api/admin/campaigns`, payload, { withCredentials: true });
        toast.success("Campaign created as draft");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (c) => {
    try {
      await axios.delete(`${API}/api/admin/campaigns/${c.campaign_id}`, { withCredentials: true });
      toast.success("Campaign deleted");
      setCampaigns((prev) => prev.filter((x) => x.campaign_id !== c.campaign_id));
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const handleSendTest = async (c) => {
    if (!testTo.trim()) {
      toast.error("Enter a test recipient email first");
      return;
    }
    setTestingId(c.campaign_id);
    try {
      await axios.post(`${API}/api/admin/campaigns/${c.campaign_id}/send-test`, { to: testTo }, { withCredentials: true });
      toast.success(`Test email sent to ${testTo}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally {
      setTestingId(null);
    }
  };

  const handleSend = async (c) => {
    setSendingId(c.campaign_id);
    try {
      const res = await axios.post(`${API}/api/admin/campaigns/${c.campaign_id}/send`, {}, { withCredentials: true });
      toast.success(`Sent to ${res.data.last_sent_count} of ${res.data.last_recipient_count} recipients`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSendingId(null);
    }
  };

  const handlePreviewHtml = async (c) => {
    try {
      const res = await axios.get(`${API}/api/admin/campaigns/${c.campaign_id}/preview-html`, { withCredentials: true });
      setHtmlPreview(res.data);
    } catch (e) {
      toast.error("Failed to render preview");
    }
  };

  const toggleListId = (list, id, max) => {
    const has = list.includes(id);
    if (has) return list.filter((x) => x !== id);
    if (max && list.length >= max) return list;
    return [...list, id];
  };

  return (
    <div className="space-y-6" data-testid="campaigns-admin-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-xl font-semibold flex items-center gap-2"><Megaphone size={20} /> Campaigns</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Author and send one-off marketing emails to your agents, with optional product promo cards.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-none" data-testid="add-campaign-btn">
          <Plus size={16} className="mr-1" /> New Campaign
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Label className="text-xs text-muted-foreground">Test recipient</Label>
        <Input
          placeholder="test-send@example.com"
          value={testTo}
          onChange={(e) => setTestTo(e.target.value)}
          className="w-64 h-9 text-sm"
          data-testid="campaign-test-recipient-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin" /></div>
      ) : campaigns.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-10 text-center text-sm text-muted-foreground">
          No campaigns yet — click "New Campaign" to create your first one.
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left">
                <th className="p-3 font-medium">Name</th>
                <th className="p-3 font-medium">Subject</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium">Last Sent</th>
                <th className="p-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaign_id} className="border-t border-border" data-testid={`campaign-row-${c.campaign_id}`}>
                  <td className="p-3 font-medium">{c.name}</td>
                  <td className="p-3 text-muted-foreground truncate max-w-[260px]">{c.subject}</td>
                  <td className="p-3">
                    {sendingId === c.campaign_id ? (
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`campaign-sending-indicator-${c.campaign_id}`}>
                        <Loader2 size={12} className="animate-spin" /> Sending...
                      </span>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "sent" ? "bg-green-100 text-green-700" : c.status === "failed" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {c.status === "sent" ? "Sent" : c.status === "failed" ? "Failed" : "Draft"}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs">
                    {c.status === "sent" || c.status === "failed"
                      ? `${c.last_sent_count}/${c.last_recipient_count} on ${new Date(c.sent_at).toLocaleDateString()}`
                      : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreviewHtml(c)}
                        title="Preview" data-testid={`campaign-preview-btn-${c.campaign_id}`}>
                        <Eye size={15} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => handleSendTest(c)} disabled={testingId === c.campaign_id}
                        title="Send test" data-testid={`campaign-test-btn-${c.campaign_id}`}>
                        {testingId === c.campaign_id ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-green-700"
                            title="Send to recipients" data-testid={`campaign-send-btn-${c.campaign_id}`}>
                            <Users size={15} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Send "{c.name}" now?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This sends a real email to every agent matching this campaign's recipient filter
                              (excluding anyone who opted out of marketing emails). This can't be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleSend(c)}
                              disabled={sendingId === c.campaign_id}
                              data-testid={`campaign-send-confirm-${c.campaign_id}`}
                            >
                              {sendingId === c.campaign_id ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                              Send Now
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)}
                        title="Edit" data-testid={`campaign-edit-btn-${c.campaign_id}`}>
                        <Pencil size={15} />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Delete"
                            data-testid={`campaign-delete-btn-${c.campaign_id}`}>
                            <Trash2 size={15} />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>This campaign will be permanently removed.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              onClick={() => handleDelete(c)}
                              data-testid={`campaign-delete-confirm-${c.campaign_id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto overflow-x-hidden"
          data-testid="campaign-dialog"
          onEscapeKeyDown={(e) => {
            // A TinyMCE toolbar/menubar dropdown is open — let it close itself
            // instead of dismissing the whole campaign dialog (and losing the
            // in-progress name/subject/body the user already typed).
            if (document.querySelector(".tox-tinymce-aux .tox-menu, .tox-tinymce-aux .tox-collection")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{form.campaign_id ? "Edit Campaign" : "New Campaign"}</DialogTitle>
            <DialogDescription>
              Saved as a draft until you click "Send Now" from the campaigns list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
              <div className="min-w-0">
                <Label className="text-xs mb-1 block">Name (internal)</Label>
                <Input ref={nameInputRef} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  data-testid="campaign-name-input" />
              </div>
              <div className="min-w-0">
                <Label className="text-xs mb-1 block">Subject</Label>
                <Input ref={subjectInputRef} value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  data-testid="campaign-subject-input" />
              </div>
            </div>

            <div className="min-w-0">
              <Label className="text-xs mb-1 block">Body</Label>
              <AdvancedEditor value={form.body_html} onChange={(v) => setForm((f) => ({ ...f, body_html: v }))} />
            </div>

            <div>
              <Label className="text-xs mb-1 block">Promo cards (one per selected product, appended after the body)</Label>
              <div className="border border-input rounded bg-background p-2 space-y-1 max-h-40 overflow-y-auto" data-testid="campaign-promo-products">
                {products.length === 0 && (
                  <p className="text-xs text-muted-foreground px-1 py-1">No products yet — add some in the Products tab.</p>
                )}
                {products.map((p) => {
                  const imgUrl = absoluteImageUrl(p.image_url);
                  return (
                    <label key={p.product_id} className="flex items-start gap-2 text-sm px-1 py-1.5 rounded cursor-pointer hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={form.promo_product_ids.includes(p.product_id)}
                        onChange={() => setForm((f) => ({ ...f, promo_product_ids: toggleListId(f.promo_product_ids, p.product_id) }))}
                        data-testid={`campaign-promo-checkbox-${p.product_id}`}
                        className="mt-1"
                      />
                      {imgUrl && (
                        <img src={imgUrl} alt={p.name} className="h-10 w-10 rounded object-cover border border-border shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div>{p.name}</div>
                        {/* Absolute addressing is what makes this image actually
                            render once the campaign leaves the website and is
                            opened in an email client — shown here so it's easy
                            to confirm before sending. */}
                        <div className="text-xs text-muted-foreground truncate" data-testid={`campaign-promo-image-url-${p.product_id}`}>
                          {imgUrl || "No image — the company logo will be used instead"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <Label className="text-xs font-medium block">Recipients</Label>
              <Select value={form.recipient_filter.mode} onValueChange={(v) => setFilter({ mode: v })}>
                <SelectTrigger data-testid="campaign-recipient-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All active agents</SelectItem>
                  <SelectItem value="date_range">Agents who joined within a date range</SelectItem>
                  <SelectItem value="exclude_purchased">Agents who haven't bought selected products</SelectItem>
                </SelectContent>
              </Select>

              {form.recipient_filter.mode === "date_range" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs mb-1 block">Joined from</Label>
                    <Input type="date" value={form.recipient_filter.date_from || ""}
                      onChange={(e) => setFilter({ date_from: e.target.value })}
                      data-testid="campaign-date-from-input" />
                  </div>
                  <div>
                    <Label className="text-xs mb-1 block">Joined to</Label>
                    <Input type="date" value={form.recipient_filter.date_to || ""}
                      onChange={(e) => setFilter({ date_to: e.target.value })}
                      data-testid="campaign-date-to-input" />
                  </div>
                </div>
              )}

              {form.recipient_filter.mode === "exclude_purchased" && (
                <div>
                  <Label className="text-xs mb-1 block">Products to exclude buyers of</Label>
                  <div className="border border-input rounded bg-background p-2 space-y-1 max-h-32 overflow-y-auto" data-testid="campaign-exclude-products">
                    {products.map((p) => (
                      <label key={p.product_id} className="flex items-center gap-2 text-sm px-1 py-1 rounded cursor-pointer hover:bg-muted">
                        <input
                          type="checkbox"
                          checked={form.recipient_filter.exclude_purchased_product_ids.includes(p.product_id)}
                          onChange={() => setFilter({
                            exclude_purchased_product_ids: toggleListId(form.recipient_filter.exclude_purchased_product_ids, p.product_id),
                          })}
                          data-testid={`campaign-exclude-checkbox-${p.product_id}`}
                        />
                        {p.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs mb-1 block">Also always include these agents</Label>
                <div className="border border-input rounded bg-background p-2 space-y-1 max-h-32 overflow-y-auto" data-testid="campaign-manual-agents">
                  {agents.map((a) => (
                    <label key={a.agent_id} className="flex items-center gap-2 text-sm px-1 py-1 rounded cursor-pointer hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={form.recipient_filter.manual_agent_ids.includes(a.agent_id)}
                        onChange={() => setFilter({ manual_agent_ids: toggleListId(form.recipient_filter.manual_agent_ids, a.agent_id) })}
                        data-testid={`campaign-manual-agent-checkbox-${a.agent_id}`}
                      />
                      {a.name} {a.email ? `(${a.email})` : ""}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="sm" onClick={handlePreviewRecipients} disabled={previewLoading}
                  data-testid="campaign-preview-recipients-btn">
                  {previewLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Users size={14} className="mr-1" />}
                  Preview Recipients
                </Button>
                {previewCount && (
                  <span className="text-sm text-muted-foreground" data-testid="campaign-recipient-count">
                    {previewCount.count} agent{previewCount.count === 1 ? "" : "s"} will receive this campaign
                  </span>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="campaign-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="campaign-save-btn">
              {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null}
              {form.campaign_id ? "Save Changes" : "Save Draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HTML preview dialog */}
      <Dialog open={!!htmlPreview} onOpenChange={(open) => !open && setHtmlPreview(null)}>
        <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="campaign-html-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview</DialogTitle>
            <DialogDescription>{htmlPreview?.subject}</DialogDescription>
          </DialogHeader>
          <div className="border border-border rounded-sm p-4 bg-white" dangerouslySetInnerHTML={{ __html: htmlPreview?.html || "" }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
